import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ReturnResult } from '../../models/return-result';
import { Helper } from '../../utility/Helper';
import { DynamicContentService } from '../../services/dynamic-content.service';
import { Page } from '../../models/paging/page';
import { DynamicContentModel } from '../../models/dynamic-content.model';


@Component({
  selector: 'app-dynamic-content-form',
  templateUrl: './dynamic-content-form.component.html',
  styleUrls: ['./dynamic-content-form.component.scss']
})
export class DynamicContentFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() label: string;
  @Input() control: FormControl;
  @Input() displayKey: string;
  @Input() searchArrayAPI: Observable<ReturnResult<any>>;
  @Input() options = [];
  @Input() isAscii: boolean = false;
  @Input() errorMessage: string;
  @Input() placeholder: string = "";
  @Input() appearance: string = "standard";
  @Input() contentType: string;
  @Output() optionSelected = new EventEmitter<any>();
  
  filteredOptions: Observable<string[]>;
  isLoading: boolean = false;
  private _destroy: Subject<void> = new Subject<void>();
  private _searchSubject: Subject<string> = new Subject<string>();
  currentPage: Page = new Page();
  disableInfiniteScroll: boolean = false;
  infiniteScrollDistance: number = 1.5;
  infiniteScrollThrottle: number = 0;
  allOptions: any[] = [];
  pageNumber: number = 0;

  constructor(private dynamicContentService: DynamicContentService) { }

  ngOnInit(): void {
    if (this.contentType) {
      this.setupDataWithPaging();
    } else {
      this.setupData();
    }
    
    this._searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this._destroy)
    ).subscribe(searchValue => {
      this.resetAndLoadData(searchValue);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const optionChange = changes.options;
    if (optionChange && optionChange.currentValue && optionChange.currentValue.length) {
      this.pushDataToStream();
    }
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
    this._searchSubject.complete();
  }

  private _filter(name: string) {
    const filterValue = name.toLowerCase();
    return this.options.filter(option => option[this.displayKey].toLowerCase().includes(filterValue));
  }
  async setupData() {
    try {
      if (this.searchArrayAPI) {
        this.isLoading = true;
        const data = await this.searchArrayAPI.toPromise();
        if (data.result) this.options = data.result;
        this.isLoading = false;
      }
      this.pushDataToStream();
    }
    catch (e) {
      console.log(e);
      this.isLoading = false;
    }
  }

  async setupDataWithPaging() {
    this.isLoading = true;
    await this.loadPageData('', 0);
    this.isLoading = false;
    this.pushDataToStream();
  }

  async loadPageData(searchValue: string, pageIndex: number) {
    const page = new Page();
    page.pageNumber = pageIndex;
    page.size = 20;
    page.searchValue = searchValue;

    const result = await this.dynamicContentService.getDynamicContentPagingByType(page, searchValue, this.contentType).toPromise();
    if (result) {
      this.currentPage = result.page;
      const newData = result.data || [];
      const totalPages = this.currentPage.size > 0 ? Math.ceil(this.currentPage.totalElements / this.currentPage.size) : 0;
      
      if (pageIndex === 0) {
        this.allOptions = newData;
        this.pageNumber = 0;
      } else {
        this.allOptions = [...this.allOptions, ...newData];
      }
      
      if (!newData?.length || this.currentPage.pageNumber >= totalPages - 1) {
        this.disableInfiniteScroll = true;
      } else {
        this.disableInfiniteScroll = false;
      }
      
      this.options = this.allOptions;
    }
  }

  resetAndLoadData(searchValue: string) {
    this._searchSubject.next(searchValue);
  }

  async onSearch(searchValue: string) {
    this.isLoading = true;
    this.pageNumber = 0;
    this.allOptions = [];
    await this.loadPageData(searchValue, 0);
    this.isLoading = false;
  }

  async onScroll() {
    if (!this.isLoading && !this.disableInfiniteScroll) {
      this.isLoading = true;
      this.pageNumber += 1;
      const searchValue = this.control.value || '';
      await this.loadPageData(searchValue, this.pageNumber);
      this.isLoading = false;
      this.pushDataToStream();
    }
  }

  pushDataToStream() {
    this.filteredOptions = this.control.valueChanges.pipe(
      takeUntil(this._destroy),
      startWith(''),
      map(value => {
        const name = typeof value === 'string' ? value : (value && value[this.displayKey]) ? value[this.displayKey] : '';
        if (this.contentType) {
          this.resetAndLoadData(name);
        }
        return name ? this._filter(name as string) : this.options.slice();
      }),
    );

    if (this.isAscii)
      this.control.valueChanges.pipe(
        takeUntil(this._destroy),
        debounceTime(1000),
      ).subscribe(() => this.control.setValue(Helper.removeNonAscii(this.control.value)));
  }

  displayFn(data) {
    if (data) {
      return data[this.displayKey] ? data[this.displayKey] : data;
    }
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.optionSelected.emit(event.option.value);
  }

  onChangeInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;
    if (value && value.length >= 3) {
      this.resetAndLoadData(value);
    }
  }
}
